import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js';
import { formatCurrency as coreFormatCurrency } from '../../../CoreFunctions';

// Helper: destroy chart if exists
function destroy(chartRef) {
  try {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  } catch (e) {}
}

const FinancingPortfolioCharts = ({ data }) => {
  // Bottom chart: Outstanding (stacked) + Total line
  const outstandingRef = useRef(null);
  const outstandingCanvasRef = useRef(null);
  // Top chart (combined): Payments + stacked(Interest, Fees) with Rate line on secondary axis
  const combinedRef = useRef(null);
  const combinedCanvasRef = useRef(null);
  // Persist chart legend visibility across re-creations
  const combinedHiddenRef = useRef({});
  const outstandingHiddenRef = useRef({});

  // Top chart cumulative toggle (applies to Payments, Interest, Fees only; rate line unchanged)
  const [cumulativeTop, setCumulativeTop] = useState(false);

  const safeArr = (v) => Array.isArray(v) ? v : [];
  // Ensure series aligns in length with labels; pad with zeros or truncate as needed
  const alignToLength = (arr, len) => {
    const out = Array.isArray(arr) ? arr.slice(0, len) : [];
    while (out.length < len) out.push(0);
    return out;
  };

  // Simple money formatter fallback if CoreFunctions import ever fails
  const fmt$ = (n) => {
    try {
      return coreFormatCurrency ? coreFormatCurrency(Number(n) || 0, 2) :
        new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
    } catch (e) {
      const x = Number(n) || 0;
      return `$${x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // Re-render charts when data or cumulative toggle changes
  useEffect(() => {
    if (!data) return;

    const labels = data.periods || [];
    const byType = data.by_type || {};
    const totals = data.totals || {};

    // Outstanding by type (bottom chart)
    const cc = alignToLength(safeArr(byType.credit_cards?.outstanding), labels.length);
    const hel = alignToLength(safeArr(byType.helocs?.outstanding), labels.length);
    const auto = alignToLength(safeArr(byType.auto_loans?.outstanding), labels.length);
    const mort = alignToLength(safeArr(byType.mortgages?.outstanding), labels.length);
    const four01k = alignToLength(safeArr(byType.four01k_loans?.outstanding), labels.length);
    const totalOutstanding = alignToLength(safeArr(totals.outstanding), labels.length);

    // Per-type rate arrays (for weighted avg rate recalculation on series toggle)
    const rateCC = alignToLength(safeArr(byType.credit_cards?.rate), labels.length);
    const rateHEL = alignToLength(safeArr(byType.helocs?.rate), labels.length);
    const rateAUTO = alignToLength(safeArr(byType.auto_loans?.rate), labels.length);
    const rateMORT = alignToLength(safeArr(byType.mortgages?.rate), labels.length);
    const rate401K = alignToLength(safeArr(byType.four01k_loans?.rate), labels.length);
    // All per-type data in order: [CC, HEL, AUTO, MORT, 401K]
    const outstandingByType = [cc, hel, auto, mort, four01k];
    const rateByType = [rateCC, rateHEL, rateAUTO, rateMORT, rate401K];

    // Helper: compute weighted avg rate (as % scale * 100) for given set of visible type indices
    const computeWeightedRate = (visibleIndices) => {
      return labels.map((_, i) => {
        let num = 0; let den = 0;
        visibleIndices.forEach(d => {
          const out = Number(outstandingByType[d][i]) || 0;
          const r = Number(rateByType[d][i]) || 0;
          num += out * r;
          den += out;
        });
        return den > 0 ? (num / den) * 100 : 0;
      });
    };

    // Top chart series (non-cumulative base)
    const weightedRate = alignToLength(safeArr(totals.weighted_avg_rate), labels.length).map(x => (x || 0) * 100);
    const totalFeesBase = alignToLength(safeArr(totals.fees), labels.length);

    // Per-type Interest arrays for tooltip breakdown (non-cumulative, per-period)
    const intCC = alignToLength(safeArr(byType.credit_cards?.interest), labels.length);
    const intHEL = alignToLength(safeArr(byType.helocs?.interest), labels.length);
    const intAUTO = alignToLength(safeArr(byType.auto_loans?.interest), labels.length);
    const intMORT = alignToLength(safeArr(byType.mortgages?.interest), labels.length);
    const int401K = alignToLength(safeArr(byType.four01k_loans?.interest), labels.length);

    // Per-type Payments arrays for tooltip breakdown (non-cumulative, per-period)
    const payCC = alignToLength(safeArr(byType.credit_cards?.payments), labels.length);
    const payHEL = alignToLength(safeArr(byType.helocs?.payments), labels.length);
    const payAUTO = alignToLength(safeArr(byType.auto_loans?.payments), labels.length);
    const payMORT = alignToLength(safeArr(byType.mortgages?.payments), labels.length);
    const pay401K = alignToLength(safeArr(byType.four01k_loans?.payments), labels.length);

    // Build cumulative arrays for top chart if toggled
    const toCumulative = (arr) => {
      const out = [];
      let run = 0;
      for (let i = 0; i < arr.length; i++) { run += (Number(arr[i]) || 0); out.push(run); }
      return out;
    };
    // totalPayments/Interest/Fees are superseded by currentTotal* variants below (which respect legend visibility)

    // Build per-type cumulative interest when in cumulative mode for tooltip breakdown
    const intCCSeries = cumulativeTop ? toCumulative(intCC) : intCC;
    const intHELSeries = cumulativeTop ? toCumulative(intHEL) : intHEL;
    const intAUTOSeries = cumulativeTop ? toCumulative(intAUTO) : intAUTO;
    const intMORTSeries = cumulativeTop ? toCumulative(intMORT) : intMORT;
    const int401KSeries = cumulativeTop ? toCumulative(int401K) : int401K;

    // Build per-type cumulative payments when in cumulative mode for tooltip breakdown
    const payCCSeries = cumulativeTop ? toCumulative(payCC) : payCC;
    const payHELSeries = cumulativeTop ? toCumulative(payHEL) : payHEL;
    const payAUTOSeries = cumulativeTop ? toCumulative(payAUTO) : payAUTO;
    const payMORTSeries = cumulativeTop ? toCumulative(payMORT) : payMORT;
    const pay401KSeries = cumulativeTop ? toCumulative(pay401K) : pay401K;

    // Determine initial visibility for all charts
    const initialHidden = outstandingHiddenRef.current || {};
    const visibleTypesIndices = [];
    for (let d = 0; d < 5; d++) {
      if (!initialHidden[d]) visibleTypesIndices.push(d);
    }

    const initialInterestBase = labels.map((_, i) => {
      let sum = 0;
      const interestByType = [intCC, intHEL, intAUTO, intMORT, int401K];
      visibleTypesIndices.forEach(d => {
        sum += (Number(interestByType[d][i]) || 0);
      });
      return sum;
    });

    const initialPaymentsBase = labels.map((_, i) => {
      let sum = 0;
      const paymentsByType = [payCC, payHEL, payAUTO, payMORT, pay401K];
      visibleTypesIndices.forEach(d => {
        sum += (Number(paymentsByType[d][i]) || 0);
      });
      return sum;
    });

    const currentTotalInterest = cumulativeTop ? toCumulative(initialInterestBase) : initialInterestBase;
    const currentTotalPayments = cumulativeTop ? toCumulative(initialPaymentsBase) : initialPaymentsBase;
    const currentTotalFees = cumulativeTop ? toCumulative(totalFeesBase) : totalFeesBase;

    // Before destroying, persist current visibility states for combined chart
    if (combinedRef.current) {
      try {
        const ch = combinedRef.current;
        const hidden = {};
        ch.data.datasets.forEach((_, idx) => {
          const visible = ch.isDatasetVisible ? ch.isDatasetVisible(idx) : true;
          hidden[idx] = !visible;
        });
        combinedHiddenRef.current = hidden;
      } catch (e) { /* ignore */ }
    }

    // Top combined chart: Payments + stacked(Interest, Fees) and Rate line
    destroy(combinedRef);
    // Prepare an external HTML tooltip for robust multi-line rendering
    // Reuse a single tooltip element per chart instance
    let tooltipEl;
    function getOrCreateTooltip(chart) {
      const canvasParent = chart.canvas.parentNode;
      let el = canvasParent.querySelector('.portfolio-tooltip');
      if (!el) {
        el = document.createElement('div');
        el.className = 'portfolio-tooltip';
        el.style.background = 'rgba(33, 33, 33, 0.95)';
        el.style.borderRadius = '6px';
        el.style.color = '#fff';
        el.style.opacity = 1;
        el.style.pointerEvents = 'none';
        el.style.position = 'absolute';
        el.style.transform = 'translate(-50%, 0)';
        el.style.transition = 'all .05s ease';
        el.style.whiteSpace = 'nowrap';
        el.style.padding = '10px 12px';
        el.style.maxWidth = '460px';
        el.style.zIndex = '50';
        el.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)';
        el.style.fontSize = '12px';
        el.style.lineHeight = '1.4';
        // content container
        const table = document.createElement('div');
        table.style.margin = '0px';
        el.appendChild(table);
        canvasParent.style.position = 'relative';
        canvasParent.appendChild(el);
      }
      return el;
    }

    function externalTooltipHandler(context) {
      const {chart, tooltip} = context;
      tooltipEl = getOrCreateTooltip(chart);

      // Hide
      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      // Build HTML content
      const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
      const title = (data.periods && data.periods[index]) ? data.periods[index] : '';
      const lines = [];
      const pctFmt = (v) => `${(Number(v) || 0).toFixed(2)}%`;
      // eslint-disable-next-line no-unused-vars
      const row = (label, valueHtml) => (
        `<div class="pt-row" style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px;">
           <span class="pt-label">${label}</span>
           <span class="pt-value" style="text-align:right; min-width: 140px; display:inline-block; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;">${valueHtml}</span>
         </div>`
      );
      const row3 = (label, valueHtml, percHtml, { indent = false } = {}) => (
        `<div class="pt-row" style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px;">
           <span class="pt-label" style="flex:1 1 auto; ${indent ? 'padding-left:12px;' : ''}">${label}</span>
           <span class="pt-value" style="text-align:right; min-width: 160px; display:inline-block; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;">${valueHtml}</span>
           <span class="pt-perc" style="text-align:right; min-width: 84px; display:inline-block; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;">${percHtml}</span>
         </div>`
      );

      // Title
      lines.push(`<div style="margin-bottom:6px;"><strong>${title}</strong></div>`);

      // Helper to push a section: main line, spacer, sub-lines, spacer
      const pushSection = (mainLabel, mainValue, subParts, isRate = false) => {
        const renderedMain = isRate ? pctFmt(mainValue) : fmt$(mainValue);
        // Use 3-column layout for main categories as well so values/percents align with sub-rows
        // For non-rate rows, show value in middle column and leave percent blank. For rate row, show percent in right column and leave value blank.
        // Per user request: Weighted Avg Rate value should appear in the same (middle) column as dollar values.
        // Therefore, put rendered percent in the middle column and leave the right-most percent column blank.
        lines.push(row3(mainLabel, renderedMain, ''));
        if (subParts && subParts.length) {
          const EPS = 0.0005;
          lines.push('<div style="height:6px"></div>');
          // Sort subcategories by descending value
          const sortedParts = [...subParts].sort((a, b) => (Number(b.val) || 0) - (Number(a.val) || 0));
          sortedParts.forEach(p => {
            const v = Number(p.val) || 0;
            if (v <= EPS) return;
            const total = isRate ? 0 : Number(mainValue) || 0;
            const pct = total ? (v / Math.abs(total)) * 100 : 0;
            const valueStr = isRate ? pctFmt(v) : fmt$(v);
            const pctStr = total ? ` (${pct.toFixed(1)}%)` : '';
            // Use three-column row for aligned currency + percent columns and indent sub-category labels
            lines.push(row3(p.name, `${valueStr}`, `${pctStr}`, { indent: true }));
          });
          lines.push('<div style="height:6px"></div>');
        } else {
          // still add spacer for visual separation from next section
          lines.push('<div style="height:6px"></div>');
        }
      };

      // Determine dataset visibility (legend state)
      // Dataset order: 0=Interest, 1=Penalties/Fees, 2=Payments, 3=Weighted Avg Rate
      const isVisible = (idx) => {
        try { return chart.isDatasetVisible ? chart.isDatasetVisible(idx) : (chart.getDatasetMeta(idx)?.hidden !== true); } catch (_) { return true; }
      };
      const showInterest = isVisible(0);
      const showFees = isVisible(1);
      const showPayments = isVisible(2);
      const showRate = isVisible(3);

      // Determine which liability types are visible in the bottom chart (used for tooltip breakdown)
      // Bottom chart dataset order: 0=Credit Cards, 1=HELOCs, 2=Auto Loans, 3=Mortgages, 4=401k Loans
      const isTypeVisible = (idx) => {
        try {
          const hidden = outstandingHiddenRef.current || {};
          return !hidden[idx];
        } catch (_) { return true; }
      };

      // Build sections in desired order: Payments, Interest, Penalties/Fees, Weighted Avg Rate
      // Payments total (cumulative or period) and sub-sources by type
      if (showPayments) {
        const paymentsTotal = Number(chart.data.datasets[2].data[index]) || 0;
        const paymentsParts = [
          { name: 'Mortgages', val: Math.abs(Number(payMORTSeries[index]) || 0), idx: 3 },
          { name: 'Auto Loans', val: Math.abs(Number(payAUTOSeries[index]) || 0), idx: 2 },
          { name: '401k Loans', val: Math.abs(Number(pay401KSeries[index]) || 0), idx: 4 },
          { name: 'HELOCs', val: Math.abs(Number(payHELSeries[index]) || 0), idx: 1 },
          { name: 'Credit Cards', val: Math.abs(Number(payCCSeries[index]) || 0), idx: 0 },
        ].filter(p => isTypeVisible(p.idx));
        pushSection('Payments', Math.abs(paymentsTotal), paymentsParts, false);
      }

      // Interest total and sub-sources by type
      if (showInterest) {
        const interestTotal = Number(chart.data.datasets[0].data[index]) || 0;
        const interestParts = [
          { name: 'Mortgages', val: Number(intMORTSeries[index]) || 0, idx: 3 },
          { name: 'Auto Loans', val: Number(intAUTOSeries[index]) || 0, idx: 2 },
          { name: '401k Loans', val: Number(int401KSeries[index]) || 0, idx: 4 },
          { name: 'HELOCs', val: Number(intHELSeries[index]) || 0, idx: 1 },
          { name: 'Credit Cards', val: Number(intCCSeries[index]) || 0, idx: 0 },
        ].filter(p => isTypeVisible(p.idx));
        pushSection('Interest', interestTotal, interestParts, false);
      }

      // Penalties / Fees (no sub-sources currently)
      if (showFees) {
        const feesTotal = Number(chart.data.datasets[1].data[index]) || 0;
        pushSection('Penalties / Fees', feesTotal, null, false);
      }

      // Weighted Avg Rate (percent; no sub-sources)
      // Read from live chart dataset (index 3) so it reflects recalculated values when bottom series are toggled
      if (showRate) {
        const rateVal = Number(chart.data.datasets[3].data[index]) || 0; // already in percent scale
        pushSection('Weighted Avg Rate (%)', rateVal, null, true);
      }

      const container = tooltipEl.firstChild;
      container.innerHTML = lines.join('');

      // Position
      const {offsetLeft: posX, offsetTop: posY} = chart.canvas;
      tooltipEl.style.opacity = 1;
      tooltipEl.style.left = posX + tooltip.caretX + 'px';
      tooltipEl.style.top = posY + tooltip.caretY + 12 + 'px';
    }

    combinedRef.current = new Chart(combinedCanvasRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          // Stacked costs on primary Y
          { label: 'Interest', data: currentTotalInterest, backgroundColor: 'rgba(255, 159, 64, 0.7)', stack: 'costs', yAxisID: 'yAmount' },
          { label: 'Penalties / Fees', data: currentTotalFees, backgroundColor: 'rgba(201, 203, 207, 0.8)', stack: 'costs', yAxisID: 'yAmount' },
          // Payments separate bar (different stack id)
          { label: 'Payments', data: currentTotalPayments, backgroundColor: 'rgba(40, 167, 69, 0.7)', stack: 'payments', yAxisID: 'yAmount' },
          // Rate line on secondary Y
          { label: 'Weighted Avg Rate (%)', data: weightedRate, type: 'line', borderColor: 'rgba(54, 162, 235, 1)', backgroundColor: 'rgba(54, 162, 235, 0.15)', yAxisID: 'yRate', fill: false, tension: 0.3 },
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            align: 'center',
            onClick: (e, legendItem, legend) => {
              // Reliable dataset toggle for Chart.js v4
              const ci = legend.chart;
              const idx = legendItem.datasetIndex;
              const wasVisible = ci.isDatasetVisible ? ci.isDatasetVisible(idx) : (ci.getDatasetMeta(idx)?.hidden !== true);
              if (ci.setDatasetVisibility) {
                ci.setDatasetVisibility(idx, !wasVisible);
              } else {
                const meta = ci.getDatasetMeta(idx);
                meta.hidden = meta.hidden === null ? true : !meta.hidden;
              }
              // Persist visibility across re-creations: store hidden flag
              try {
                if (!combinedHiddenRef.current) combinedHiddenRef.current = {};
                const nowVisible = ci.isDatasetVisible ? ci.isDatasetVisible(idx) : (ci.getDatasetMeta(idx)?.hidden !== true);
                combinedHiddenRef.current[idx] = !nowVisible;
              } catch (_) {}
              ci.update();
            }
          },
          tooltip: {
            enabled: false,
            mode: 'index',
            intersect: false,
            external: externalTooltipHandler,
          }
        },
        layout: { padding: { left: 8, right: 8 } },
        scales: {
          x: { stacked: true, type: 'category', offset: true, alignToPixels: true },
          yAmount: { type: 'linear', position: 'left', beginAtZero: true, stacked: true, ticks: { callback: (v) => `$${Number(v).toLocaleString()}` }, title: { display: false } },
          yRate: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, ticks: { display: false }, border: { display: false }, title: { display: false } },
        },
        interaction: { mode: 'index', intersect: false },
        datasets: { bar: { categoryPercentage: 0.9, barPercentage: 0.9, borderWidth: 0 } },
        }
    });

    // Reapply persisted visibility states after chart creation
    try {
      const hidden = combinedHiddenRef.current || {};
      Object.keys(hidden).forEach(k => {
        const idx = Number(k);
        if (hidden[k] && combinedRef.current) {
          combinedRef.current.setDatasetVisibility(idx, false);
        }
      });
      if (combinedRef.current) combinedRef.current.update();
    } catch (e) { /* ignore */ }

    // Bottom Outstanding stacked + total line
    destroy(outstandingRef);
    outstandingRef.current = new Chart(outstandingCanvasRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          { label: 'Credit Cards', data: cc, backgroundColor: 'rgba(75, 192, 192, 0.6)', stack: 'stack1' },
          { label: 'HELOCs', data: hel, backgroundColor: 'rgba(255, 205, 86, 0.6)', stack: 'stack1' },
          { label: 'Auto Loans', data: auto, backgroundColor: 'rgba(153, 102, 255, 0.6)', stack: 'stack1' },
          { label: 'Mortgages', data: mort, backgroundColor: 'rgba(54, 162, 235, 0.6)', stack: 'stack1' },
          { label: '401k Loans', data: four01k, backgroundColor: 'rgba(255, 99, 132, 0.6)', stack: 'stack1' },
          { label: 'Total Outstanding', data: totalOutstanding, type: 'line', borderColor: 'rgba(220,53,69,1)', backgroundColor: 'rgba(220,53,69,0.1)', yAxisID: 'yLeft', fill: false, tension: 0.3 },
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            align: 'center',
            onClick: (e, legendItem, legend) => {
              const ci = legend.chart;
              const idx = legendItem.datasetIndex;
              const wasVisible = ci.isDatasetVisible(idx);
              ci.setDatasetVisibility(idx, !wasVisible);

              // Persist visibility across re-creations
              try {
                if (!outstandingHiddenRef.current) outstandingHiddenRef.current = {};
                const nowVisible = ci.isDatasetVisible(idx);
                outstandingHiddenRef.current[idx] = !nowVisible;
              } catch (_) {}

              // Recalculate Total Outstanding (idx 5)
              // We sum only the core data sources (idx 0 to 4) if they are visible
              const totalIdx = 5;
              const ds = ci.data.datasets;
              const visibleTypesIndices = [];
              for (let d = 0; d < 5; d++) {
                if (ci.isDatasetVisible(d)) {
                  visibleTypesIndices.push(d);
                }
              }

              const newTotal = labels.map((_, i) => {
                let sum = 0;
                visibleTypesIndices.forEach(d => {
                  sum += (Number(ds[d].data[i]) || 0);
                });
                return sum;
              });
              ds[totalIdx].data = newTotal;
              ci.update();

              // Also update the top chart (combinedRef) based on the same visible indices
              if (combinedRef.current) {
                const topChart = combinedRef.current;
                const topDs = topChart.data.datasets;
                // topDs indices: 0=Interest, 1=Penalties/Fees, 2=Payments, 3=Weighted Avg Rate

                // Interest (idx 0)
                const interestByType = [intCC, intHEL, intAUTO, intMORT, int401K];
                const newInterestBase = labels.map((_, i) => {
                  let sum = 0;
                  visibleTypesIndices.forEach(d => {
                    sum += (Number(interestByType[d][i]) || 0);
                  });
                  return sum;
                });
                topDs[0].data = cumulativeTop ? toCumulative(newInterestBase) : newInterestBase;

                // Payments (idx 2)
                const paymentsByType = [payCC, payHEL, payAUTO, payMORT, pay401K];
                const newPaymentsBase = labels.map((_, i) => {
                  let sum = 0;
                  visibleTypesIndices.forEach(d => {
                    sum += (Number(paymentsByType[d][i]) || 0);
                  });
                  return sum;
                });
                topDs[2].data = cumulativeTop ? toCumulative(newPaymentsBase) : newPaymentsBase;

                // Weighted Avg Rate (idx 3) - Recalculate using per-type outstanding and rate arrays
                topDs[3].data = computeWeightedRate(visibleTypesIndices);

                topChart.update();
              }
            }
          }
        },
        layout: { padding: { left: 8, right: 8 } },
        scales: {
          x: { stacked: true, type: 'category', offset: true, alignToPixels: true },
          yLeft: { type: 'linear', position: 'left', beginAtZero: true, ticks: { callback: (v) => `$${Number(v).toLocaleString()}` } },
          yRight: { type: 'linear', position: 'right', display: true, ticks: { display: false }, grid: { drawOnChartArea: false }, border: { display: false } },
          y: { stacked: true, display: false },
        },
        interaction: { mode: 'index', intersect: false },
        datasets: { bar: { categoryPercentage: 0.9, barPercentage: 0.9, borderWidth: 0 } },
      }
    });

    // Reapply persisted visibility and initial dynamic total for bottom chart
    try {
      const hidden = outstandingHiddenRef.current || {};
      const ci = outstandingRef.current;
      if (ci) {
        Object.keys(hidden).forEach(k => {
          const idx = Number(k);
          if (hidden[k]) ci.setDatasetVisibility(idx, false);
        });

        // Recalculate initial total if some datasets were hidden by persistence
        if (Object.keys(hidden).length > 0) {
          const totalIdx = 5;
          const ds = ci.data.datasets;
          const newTotal = labels.map((_, i) => {
            let sum = 0;
            for (let d = 0; d < 5; d++) {
              if (ci.isDatasetVisible(d)) {
                sum += (Number(ds[d].data[i]) || 0);
              }
            }
            return sum;
          });
          ds[totalIdx].data = newTotal;
        }
        ci.update();
      }
    } catch (e) { /* ignore */ }

    return () => {
      destroy(combinedRef);
      destroy(outstandingRef);
    };
  }, [data, cumulativeTop]);

  if (!data) return null;

  return (
    <div style={{ marginTop: '8px', width: '100%' }}>
      {/* Top combined chart: smaller height */}
      <div style={{ width: '100%', marginBottom: '32px', height: 220 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 4 }}>
          <button
            className={`btn ${cumulativeTop ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setCumulativeTop(v => !v)}
            title="Toggle cumulative mode for Payments, Interest, and Fees (top chart only)"
          >
            Cumulative
          </button>
        </div>
        <canvas ref={combinedCanvasRef} style={{ width: '100%', height: '100%' }}/>
      </div>
      {/* Bottom outstanding chart: ~2x taller than top */}
      <div style={{ width: '100%', marginBottom: '16px', height: 560 }}>
        <canvas ref={outstandingCanvasRef} style={{ width: '100%', height: '100%' }}/>
      </div>
    </div>
  );
};

export default FinancingPortfolioCharts;
